import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { errorText } from '../utils/errorText';
import { fieldStore } from '../utils/fieldBatches';
import { fieldOutbox } from '../utils/fieldOutbox';
import { getWorkspace } from '../utils/workspace';
import { useHtmlDir } from '../components/organic/motion';
import MapCanvas from '../components/map/MapCanvas';
import PointDrawer from '../components/map/PointDrawer';
import ObservationWizard from '../components/map/ObservationWizard';
import FieldFixOffer from '../components/field/FieldFixOffer';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import PhotoGallery from '../components/PhotoGallery';
import useUnsentBatches, { serverIdsOf } from '../components/field/useUnsentBatches';
import { useGeoWatch } from '../components/field/useGeoWatch';
import { bearingLabel, distanceLabel, towards } from '../utils/geo';
import { clearActiveTarget, isTargetOf, setActiveTarget, useActiveTarget } from '../utils/activeTarget';
import useRoute from '../utils/routing/useRoute';
import { useTravelMode } from '../utils/routing/travelMode';
import RoutePanel from '../components/map/RoutePanel';
import SaveAreaButton from '../components/map/SaveAreaButton';

/*
 * The national map of identifications and collections (מפת זיהויים ואיסופים ארצית).
 *
 * Pins come from GET /map/points (routes/map_points.py), which decides what is
 * an observation and what a collection, what is verified, and who sees what.
 * To them are added the person's own records still on this device: a pin
 * dropped with no signal is on the map at once, grey and dashed, and becomes
 * an ordinary pin when the outbox has sent it.
 *
 * Adding: "Drop a pin" then a tap, or a long press anywhere, places a pin and
 * asks what it is -- an observation (seen, not collected: no specimen code) or
 * a collection (a specimen code is minted). Either opens ObservationWizard.
 *
 * From Field Mode: the camera's Map chip and the gallery's map button come
 * here with state fromField, and "Back to Field Mode" goes back through that
 * history entry (an installed app has no browser Back); it is offered too to
 * someone whose workspace is Field, opened here any other way. A camera
 * specimen saved before the phone found itself is still given a fix here
 * while the outbox waits for one (FieldFixOffer).
 *
 * Opened with router state:
 *   verification  the first filter; /public-sightings redirects here with
 *                 'pending', the sightings awaiting review (App.js);
 *   focus         {lat, lng, id}: Home's "Show on map" for one record. The map
 *                 is asked to centre there (MapCanvas `focus`) and the record's
 *                 own drawer opens once the points are in.
 *
 * A target to walk to (utils/activeTarget.js): a pin's drawer offers "Set as
 * target", and a dropped pin "Set as target here". While one is set, a banner
 * says what it is, how far and which way it lies from the phone's live fix
 * (drawn on the map as a dot, its accuracy and a line to the target), and
 * offers to clear it or to go to Field Mode, whose camera shows the way in a
 * mini-map and stamps every new specimen with the target. The phone's position
 * is followed only while a target is set or "Navigate" is pressed: opening the
 * map never asks for the location by itself.
 *
 * Public sightings are reviewed in the pin's drawer (PointDrawer). One sent
 * with no location has no pin, so reviewers (routes/public_survey.py CM_ROLES)
 * get a chip here for those, listing them from GET /public-sightings with the
 * same actions.
 *
 * `store` and `outbox` default to the page's own (Field Mode's); tests pass theirs.
 */
const TYPES = ['all', 'observation', 'collection'];
const VERIFICATIONS = ['all', 'verified', 'unverified', 'pending'];
const EMPTY = { type: 'all', verification: 'all', from: '', to: '' };
// routes/public_survey.py CM_ROLES: who reviews public sightings.
const REVIEW_ROLES = ['collection_manager', 'internal_expert'];

/** The first filters, from the router state a redirect or a link brought. */
function initialFilters(state) {
  const verification = state?.verification;
  return VERIFICATIONS.includes(verification) ? { ...EMPTY, verification } : EMPTY;
}

/** A place to centre on, or null when the state does not name a real one. */
export function focusOf(state) {
  const f = state?.focus;
  const lat = Number(f?.lat);
  const lng = Number(f?.lng);
  if (f?.lat == null || f?.lng == null || !Number.isFinite(lat) || !Number.isFinite(lng)
      || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng, id: f.id != null ? String(f.id) : null };
}

/** A batch still on this device, as a pin. */
export function localPoint(batch) {
  const loc = batch.location;
  if (!loc || !Number.isFinite(Number(loc.lat)) || !Number.isFinite(Number(loc.lng))) return null;
  return {
    id: `local-${batch.id}`,
    local: true,
    lat: Number(loc.lat), lng: Number(loc.lng),
    kind: batch.details?.observeOnly ? 'observation' : 'collection',
    source: 'field',
    verification: 'unverified',
    taxon_name: batch.details?.taxon_name || null,
    collected_at: batch.startedAt || batch.createdAt,
    notes: batch.details?.general_notes || null,
    thumbnail: batch.thumbnail || null,
    photo_count: (batch.photos || []).length,
    status: batch.status,
  };
}

export { unsent } from '../components/field/useUnsentBatches';

export default function NationalMap({ store: injectedStore, outbox: injectedOutbox }) {
  const { t } = useTranslation(['map', 'common']);
  const { user } = useAuth() || {};
  const userId = user?.user_id;
  const store = useMemo(() => injectedStore || fieldStore(), [injectedStore]);
  const outbox = useMemo(() => injectedOutbox || fieldOutbox(), [injectedOutbox]);
  const navigate = useNavigate();
  const location = useLocation();
  const dir = useHtmlDir();

  // Back to the Field Mode screen this came from: the entry before, so the
  // camera opens again on its unfinished specimen. Opened any other way, the
  // camera takes this entry's place (useFieldNav.useBackToGallery's rule).
  const fromField = !!location.state?.fromField;
  const showToField = fromField || getWorkspace(user) === 'field';
  const toField = () => (fromField ? navigate(-1) : navigate('/field', { replace: true }));
  const isReviewer = (user?.roles || []).some(r => REVIEW_ROLES.includes(r));
  // Read once: the state of the entry this page was opened with.
  const [focus] = useState(() => focusOf(location.state));
  const [reviewQueue] = useState(() => location.state?.verification === 'pending');

  const [filters, setFilters] = useState(() => initialFilters(location.state));
  const [points, setPoints] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine !== false));
  const [backOnline, setBackOnline] = useState(false);
  const [dropMode, setDropMode] = useState(false);
  const [dropped, setDropped] = useState(null);
  const [wizard, setWizard] = useState(null); // { pin, mode }
  const [selected, setSelected] = useState(null);
  const [notice, setNotice] = useState('');
  // On a phone the filters fold away so the map has the screen; open on wider ones.
  const [filtersOpen, setFiltersOpen] = useState(() => (typeof window === 'undefined' || !window.matchMedia
    ? true : window.matchMedia('(min-width: 700px)').matches));

  // The target being walked to, and the phone's own position: followed only
  // while there is a target or "Navigate" is pressed (an opt-in, never on load).
  const target = useActiveTarget(userId);
  const [navigating, setNavigating] = useState(false);
  const following = !!target || navigating;
  const { fix: you, state: youState } = useGeoWatch(following);
  const way = towards(you, target);
  // The way along roads and trails (utils/routing): offline once its data is
  // on the device; the straight line when there is no route.
  const [travelMode, setTravelMode] = useTravelMode();
  const routeState = useRoute({ target, fix: you, mode: travelMode, enabled: !!target });
  const makeTarget = (where) => {
    setActiveTarget({ targetId: where.local ? null : where.id ?? null, lat: where.lat, lng: where.lng,
      label: where.taxon_name || where.code || '', userId });
  };
  const goToField = () => (fromField ? navigate(-1) : navigate('/field', { state: { returnTo: '/map' } }));

  const query = useMemo(() => Object.fromEntries(Object.entries(filters).filter(([, v]) => v && v !== 'all')),
    [filters]);
  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/map/points', { params: query });
      setPoints(data.points || []);
      setTruncated(!!data.truncated);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setLoading(false);
    }
  }, [query]);
  useEffect(() => { load(); }, [load]);

  // The focused record's drawer, once, after the first load: a pin that turns
  // up later under another filter is not opened by surprise.
  const focusDone = useRef(!focus?.id);
  useEffect(() => {
    if (focusDone.current || loading) return;
    focusDone.current = true;
    const hit = points.find(p => String(p.id) === focus.id);
    if (hit) setSelected(hit);
  }, [focus, points, loading]);

  // The person's records on this device, not the camera's unfinished one
  // (Save has not been tapped: it is not waiting to be sent). When one is sent
  // in full it leaves this list and the server's is read again; until then
  // its pin is the device's, and the server's half-made draft is not drawn.
  const batches = useUnsentBatches(store, userId, load).filter(b => b.status !== 'active');
  const inProgress = useMemo(() => serverIdsOf(batches), [batches]);
  const shownPoints = useMemo(() => points.filter(p => !inProgress.has(p.id)), [points, inProgress]);
  useEffect(() => { outbox.start?.(); }, [outbox]);

  useEffect(() => {
    const up = () => { setOnline(true); setBackOnline(true); };
    const down = () => { setOnline(false); setBackOnline(false); };
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', down); };
  }, []);

  const localPoints = useMemo(() => batches.map(localPoint).filter(Boolean)
    .filter(p => filters.type === 'all' || p.kind === filters.type)
    .filter(() => filters.verification === 'all' || filters.verification === 'unverified'), [batches, filters]);

  const set = key => e => setFilters(f => ({ ...f, [key]: e.target.value }));
  const place = useCallback((pin) => {
    setSelected(null);
    setWizard(null);
    setDropped(pin);
    setDropMode(false);
  }, []);
  const start = mode => { setWizard({ pin: dropped, mode }); };
  const cancelPin = () => { setDropped(null); setWizard(null); };
  const syncNow = () => { setBackOnline(false); outbox.run(); };

  return (
    <div>
      {userId && <FieldFixOffer store={store} outbox={outbox} userId={userId} />}
      <div style={titleRow}>
        {showToField && (
          <button type="button" onClick={toField} title={t('tip.backToField')} data-testid="map-to-field"
            style={{ ...actionBtn, background: 'var(--c-primary)', color: '#fff' }}>
            <span aria-hidden="true">{dir === 'rtl' ? '→' : '←'}</span> {t('backToField')}
          </button>
        )}
        <h2 style={{ margin: 0 }}>{t('pageTitle')}</h2>
      </div>
      <p style={{ margin: '0 0 10px', color: 'var(--c-text-muted-fg)', fontSize: 14 }}>{t('intro')}</p>

      <button type="button" onClick={() => setFiltersOpen(o => !o)} aria-expanded={filtersOpen}
        aria-controls="map-filters" title={t('tip.filters')} style={{ ...smallBtn, marginBottom: 6 }}
        data-testid="filters-toggle">
        {t('filters.label')}{Object.keys(query).length ? ` (${Object.keys(query).length})` : ''} {filtersOpen ? '▴' : '▾'}
      </button>
      {filtersOpen && (
      <form id="map-filters" onSubmit={e => e.preventDefault()} style={filterBar}
        aria-label={t('filters.label')} data-testid="map-filters">
        <label style={field}>{t('filters.type')}
          <select value={filters.type} onChange={set('type')} title={t('tip.filterType')} style={input}
            data-testid="filter-type">
            {TYPES.map(v => <option key={v} value={v}>{t(`filters.types.${v}`)}</option>)}
          </select>
        </label>
        <label style={field}>{t('filters.verification')}
          <select value={filters.verification} onChange={set('verification')} title={t('tip.filterVerification')}
            style={input} data-testid="filter-verification">
            {VERIFICATIONS.map(v => <option key={v} value={v}>{t(`verification.${v}`)}</option>)}
          </select>
        </label>
        <label style={field}>{t('filters.from')}
          <input type="date" value={filters.from} max={filters.to || undefined} onChange={set('from')} style={input}
            title={t('tip.filterFrom')} data-testid="filter-from" />
        </label>
        <label style={field}>{t('filters.to')}
          <input type="date" value={filters.to} min={filters.from || undefined} onChange={set('to')} style={input}
            title={t('tip.filterTo')} data-testid="filter-to" />
        </label>
        {JSON.stringify(filters) !== JSON.stringify(EMPTY) && (
          <button type="button" onClick={() => setFilters(EMPTY)} style={{ ...smallBtn, alignSelf: 'flex-end' }}
            title={t('tip.filterClear')} data-testid="filter-clear">{t('filters.clear')}</button>
        )}
      </form>
      )}

      <div style={toolbar}>
        <button type="button" aria-pressed={dropMode} onClick={() => setDropMode(d => !d)} data-testid="drop-pin"
          title={t('tip.dropPin')}
          style={{ ...actionBtn, background: dropMode ? 'var(--c-primary)' : 'var(--c-surface)',
            color: dropMode ? '#fff' : 'var(--c-text-fg)' }}>
          📍 {dropMode ? t('dropPin.on') : t('dropPin.off')}
        </button>
        {!target && (
          <button type="button" aria-pressed={navigating} onClick={() => setNavigating(n => !n)} data-testid="map-navigate"
            title={t('tip.navigate')}
            style={{ ...actionBtn, background: navigating ? 'var(--c-primary)' : 'var(--c-surface)',
              color: navigating ? '#fff' : 'var(--c-text-fg)' }}>
            🧭 {t('target.navigate')}
          </button>
        )}
        <span style={{ fontSize: 13, color: 'var(--c-text-muted-fg)' }} data-testid="map-count">
          {loading ? t('common:loading') : t('count', { count: shownPoints.length + localPoints.length })}
        </span>
        <Legend t={t} />
      </div>

      {!online && <p role="status" style={banner('var(--c-warning-fg)')} data-testid="map-offline">{t('offline')}</p>}
      {online && batches.length > 0 && (
        <p role="status" style={banner('var(--c-info)')} data-testid="map-pending">
          {t(backOnline ? 'pending.backOnline' : 'pending.waiting', { count: batches.length })}{' '}
          <button type="button" onClick={syncNow} style={smallBtn} title={t('tip.sync')}
            data-testid="map-sync">{t('pending.sync')}</button>
        </p>
      )}
      {truncated && <p role="status" style={banner('var(--c-warning-fg)')} data-testid="map-truncated">{t('truncated')}</p>}
      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }} data-testid="map-error">{error}</p>}
      {notice && <p role="status" style={{ color: 'var(--c-success-fg)', margin: '4px 0' }} data-testid="map-notice">{notice}</p>}

      {target && (
        <section aria-label={t('target.banner', { label: target.label || t('target.unnamed') })} style={targetBanner}
          data-testid="map-target" data-target={target.targetId || ''}>
          <strong style={{ overflowWrap: 'anywhere' }}>
            <span aria-hidden="true">🎯 </span>{t('target.banner', { label: target.label || t('target.unnamed') })}
          </strong>
          <span style={{ fontSize: 14 }} data-testid="map-target-distance" data-state={way?.arrived ? 'arrived'
            : way ? 'ok' : youState}>
            {way?.arrived && t('target.arrived')}
            {way && !way.arrived && (
              <>
                <span style={srLabel}>{t('target.distanceLabel')}: </span>
                <bdi>{t('target.distance', { distance: distanceLabel(way.km, t), bearing: bearingLabel(way.bearing, t) })}</bdi>
              </>
            )}
            {!way && (youState === 'none' ? t('target.noGps') : t('target.waiting'))}
          </span>
          <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginInlineStart: 'auto' }}>
            <button type="button" onClick={goToField} title={t('tip.target.toField')} data-testid="map-target-field"
              style={{ ...smallBtn, background: 'var(--c-primary)', color: '#fff', borderColor: 'var(--c-primary)' }}>
              {t('target.toField')}
            </button>
            <button type="button" onClick={() => { clearActiveTarget(); setNavigating(false); }} style={smallBtn}
              title={t('tip.target.clear')} data-testid="map-target-clear">{t('target.clear')}</button>
          </span>
          <RoutePanel state={routeState} mode={travelMode} onMode={setTravelMode} />
          <SaveAreaButton center={target} label={target.label || t('target.unnamed')} />
        </section>
      )}
      {!target && navigating && (
        <p role="status" style={banner('var(--c-info)')} data-testid="map-navigating" data-state={youState}>
          🧭 {youState === 'ok' ? t('target.navigating') : youState === 'none' ? t('target.noGps') : t('target.waiting')}
        </p>
      )}

      {isReviewer && (
        <NoLocationSightings t={t} defaultOpen={reviewQueue}
          canRegister={(user?.roles || []).includes('collection_manager')} />
      )}

      <div style={mapBox}>
        <MapCanvas points={shownPoints} localPoints={localPoints} dropMode={dropMode} dropped={dropped}
          fitKey={JSON.stringify(query)} focus={focus} label={t('mapLabel')}
          you={following ? you : null} target={target} route={routeState.route}
          onSelect={(p) => { setDropped(null); setWizard(null); setSelected(p); }} onPlace={place} />
        {dropMode && !dropped && <p style={hint} data-testid="drop-hint">{t('dropPin.hint')}</p>}

        {dropped && !wizard && (
          <div role="dialog" aria-modal="false" aria-labelledby="pin-choice-title" style={choice} data-testid="pin-choice">
            <strong id="pin-choice-title">{t('pinChoice.title')}</strong>
            <span style={{ fontSize: 12, color: 'var(--c-text-muted-fg)' }}>
              {Number(dropped.lat).toFixed(5)}, {Number(dropped.lng).toFixed(5)}</span>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" onClick={() => start('observation')} style={btn('var(--c-primary)')}
                title={t('tip.newObservation')} data-testid="new-observation">{t('pinChoice.observation')}</button>
              <button type="button" onClick={() => start('collection')} style={btn('var(--c-success)')}
                title={t('tip.newCollection')} data-testid="new-collection">{t('pinChoice.collection')}</button>
              <button type="button" onClick={() => { makeTarget({ lat: dropped.lat, lng: dropped.lng, local: true }); cancelPin(); }}
                style={btn('#d81b60')} title={t('tip.target.setHere')} data-testid="pin-target">
                🎯 {t('target.setHere')}</button>
              <button type="button" onClick={cancelPin} style={btn('var(--c-text-muted)')}
                title={t('tip.pinCancel')} data-testid="pin-cancel">{t('common:cancel')}</button>
            </div>
          </div>
        )}

        {wizard && userId && (
          <ObservationWizard pin={wizard.pin} mode={wizard.mode} store={store} outbox={outbox} userId={userId} user={user}
            online={online} onClose={cancelPin} targetId={target?.targetId || null}
            onSaved={(_batch, { queued }) => {
              setWizard(null);
              setDropped(null);
              // Its pin comes from the store's own change (useUnsentBatches).
              setNotice(t(queued ? 'saved.offline' : 'saved.online'));
            }} />
        )}

        {selected && (
          <PointDrawer point={selected} onClose={() => setSelected(null)} onChanged={load}
            isTarget={isTargetOf(target, selected)} onSetTarget={makeTarget} onClearTarget={clearActiveTarget} />
        )}
      </div>
    </div>
  );
}

/*
 * Public sightings sent with no location: no pin, so they are listed here for
 * their reviewers, with what the drawer offers a pinned one -- confirm, delete
 * (soft), and once confirmed, register as a lab sample (managers, as
 * routes/map_points.py's register_public_sample). Each one's photos are read
 * when asked for: a reviewer confirms a stranger's report from them.
 */
function NoLocationSightings({ t, defaultOpen, canRegister }) {
  const [rows, setRows] = useState(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(!!defaultOpen);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [asking, setAsking] = useState(null); // { kind: 'delete' | 'register', id }
  const [askError, setAskError] = useState('');
  const [photos, setPhotos] = useState({}); // sample_id -> array | 'loading' | 'error'
  const [showing, setShowing] = useState(null);

  const load = useCallback(() => api.get('/public-sightings')
    .then(({ data }) => {
      setFailed(false);
      setRows((data || []).filter(s => s.gps_lat == null || s.gps_lng == null));
    })
    .catch(() => setFailed(true)), []);
  useEffect(() => { load(); }, [load]);

  const togglePhotos = (id) => {
    setShowing(s => (s === id ? null : id));
    if (photos[id] !== undefined) return;
    setPhotos(p => ({ ...p, [id]: 'loading' }));
    api.get(`/collections/${id}/photos`)
      .then(({ data }) => setPhotos(p => ({ ...p, [id]: data || [] })))
      .catch(() => setPhotos(p => ({ ...p, [id]: 'error' })));
  };
  const confirm = async (id) => {
    setBusy(true);
    setError('');
    try {
      await api.put(`/public-sightings/${id}/confirm`);
      await load();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const runAsked = async () => {
    setBusy(true);
    setAskError('');
    try {
      if (asking.kind === 'delete') await api.delete(`/public-sightings/${asking.id}`);
      else await api.put(`/public-sightings/${asking.id}/register-lab-sample`);
      setAsking(null);
      await load();
    } catch (e) {
      setAskError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  if (failed) return <p style={{ ...mutedLine, color: 'var(--c-danger-fg)' }} data-testid="no-location-failed">{t('noLocation.loadFailed')}</p>;
  if (!rows || rows.length === 0) return null;
  return (
    <div style={{ margin: '6px 0' }}>
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} aria-controls="no-location-list"
        title={t('tip.noLocation')} style={{ ...smallBtn, borderColor: 'var(--c-warning-fg)' }} data-testid="no-location-chip">
        <span aria-hidden="true">⚠ </span>{t('noLocation.chip', { count: rows.length })} {open ? '▴' : '▾'}
      </button>
      {open && (
        <section id="no-location-list" aria-label={t('noLocation.title')} style={noLocationBox} data-testid="no-location-list">
          <p style={{ ...mutedLine, marginTop: 0 }}>{t('noLocation.intro')}</p>
          {error && <p role="alert" style={{ color: 'var(--c-danger-fg)', margin: '4px 0' }}>{error}</p>}
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {rows.map(s => {
              const when = s.collection_datetime ? new Date(s.collection_datetime) : null;
              const shown = photos[s.sample_id];
              return (
                <li key={s.sample_id} style={noLocationRow} data-testid={`no-location-${s.sample_id}`}>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
                    <strong>{s.current_taxon_name || t('drawer.unidentified')}</strong>
                    <span style={statusPill(s.review_status === 'confirmed')}>
                      {t(s.review_status === 'confirmed' ? 'noLocation.confirmed' : 'verification.pending')}
                    </span>
                  </div>
                  <div style={mutedLine}>
                    {[when && !Number.isNaN(when.getTime()) ? when.toLocaleString() : null, s.region,
                      s.submitter_email && t('noLocation.sentBy', { name: s.submitter_name || '—', email: s.submitter_email })]
                      .filter(Boolean).join(' · ')}
                  </div>
                  {s.general_notes && <p style={{ margin: '4px 0', whiteSpace: 'pre-wrap', fontSize: 14 }}>{s.general_notes}</p>}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                    <button type="button" onClick={() => togglePhotos(s.sample_id)} aria-expanded={showing === s.sample_id}
                      title={t('tip.noLocationPhotos')} style={smallBtn} data-testid={`no-location-photos-${s.sample_id}`}>
                      {t(showing === s.sample_id ? 'noLocation.hidePhotos' : 'noLocation.photos')}
                    </button>
                    {s.review_status !== 'confirmed' && (
                      <button type="button" disabled={busy} onClick={() => confirm(s.sample_id)} style={smallBtn}
                        title={t('tip.drawer.confirmSighting')} data-testid={`no-location-confirm-${s.sample_id}`}>
                        {t('drawer.confirmSighting')}
                      </button>
                    )}
                    {s.review_status === 'confirmed' && canRegister && (
                      <button type="button" disabled={busy} style={smallBtn}
                        onClick={() => { setAskError(''); setAsking({ kind: 'register', id: s.sample_id }); }}
                        title={t('tip.drawer.register')} data-testid={`no-location-register-${s.sample_id}`}>
                        {t('drawer.registerPublic')}
                      </button>
                    )}
                    <button type="button" disabled={busy} style={{ ...smallBtn, color: 'var(--c-danger-fg)' }}
                      onClick={() => { setAskError(''); setAsking({ kind: 'delete', id: s.sample_id }); }}
                      title={t('tip.drawer.deleteSighting')} data-testid={`no-location-delete-${s.sample_id}`}>
                      {t('drawer.deleteSighting')}
                    </button>
                  </div>
                  {showing === s.sample_id && (
                    <div style={{ marginTop: 8 }}>
                      {shown === 'loading' && <span style={mutedLine}>{t('common:loading')}</span>}
                      {shown === 'error' && <span style={{ ...mutedLine, color: 'var(--c-danger-fg)' }}>{t('noLocation.photosFailed')}</span>}
                      {Array.isArray(shown) && (shown.length === 0
                        ? <span style={mutedLine}>{t('noLocation.noPhotos')}</span>
                        : <PhotoGallery photos={shown} />)}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}
      <ConfirmDeleteModal
        open={!!asking}
        title={asking?.kind === 'register' ? t('drawer.registerTitle') : t('drawer.deleteSightingTitle')}
        consequences={[asking?.kind === 'register' ? t('drawer.registerConsequence') : t('drawer.deleteSightingConsequence')]}
        deleting={busy}
        error={askError}
        confirmLabel={asking?.kind === 'register' ? t('drawer.registerPublic') : undefined}
        confirmingLabel={asking?.kind === 'register' ? t('drawer.registering') : undefined}
        confirmColor={asking?.kind === 'register' ? 'var(--c-success)' : undefined}
        onConfirm={runAsked}
        onCancel={() => setAsking(null)}
      />
    </div>
  );
}

function Legend({ t }) {
  const items = [['observation', '#1e88e5', '50%'], ['collection', '#2e7d32', '3px'], ['public', '#ef6c00', '50%'],
    ['local', '#757575', '50%']];
  return (
    <ul style={legend} aria-label={t('legend.label')} data-testid="map-legend">
      {items.map(([k, color, radius]) => (
        <li key={k} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span aria-hidden="true" style={{ width: 12, height: 12, background: color, borderRadius: radius,
            border: k === 'local' ? '1px dashed #fff' : 'none', display: 'inline-block' }} />
          {t(`kind.${k}`)}
        </li>
      ))}
      <li style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span aria-hidden="true">✓</span>{t('verification.verified')}</li>
    </ul>
  );
}

const titleRow = { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', margin: '0 0 6px' };
const filterBar = { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', padding: 10, borderRadius: 8,
  border: '1px solid var(--c-border)', background: 'var(--c-bg-subtle)', marginBottom: 8 };
const field = { display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12, fontWeight: 600, flex: '1 1 140px', minWidth: 0 };
const input = { padding: '7px 8px', borderRadius: 6, border: '1px solid var(--c-border)', fontSize: 14,
  background: 'var(--c-surface)', color: 'var(--c-text-fg)', minHeight: 38, boxSizing: 'border-box', width: '100%' };
const toolbar = { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', margin: '6px 0' };
const actionBtn = { padding: '8px 14px', minHeight: 44, borderRadius: 999, border: '1px solid var(--c-border)', fontSize: 14,
  cursor: 'pointer' };
const smallBtn = { padding: '6px 12px', minHeight: 36, borderRadius: 6, border: '1px solid var(--c-border)',
  background: 'var(--c-surface)', color: 'var(--c-text-fg)', cursor: 'pointer', fontSize: 13 };
const legend = { display: 'flex', gap: 10, flexWrap: 'wrap', listStyle: 'none', margin: 0, padding: 0, fontSize: 12,
  marginInlineStart: 'auto' };
const mutedLine = { margin: '2px 0', fontSize: 13, color: 'var(--c-text-muted-fg)' };
const noLocationBox = { margin: '6px 0', padding: 10, borderRadius: 8, border: '1px solid var(--c-border)',
  background: 'var(--c-bg-subtle)' };
const noLocationRow = { padding: '8px 0', borderTop: '1px solid var(--c-border)', minWidth: 0, overflowWrap: 'anywhere' };
const statusPill = confirmed => ({ fontSize: 12, padding: '1px 8px', borderRadius: 999, border: '1px solid var(--c-border)',
  background: confirmed ? 'var(--c-success-soft)' : 'var(--c-warning-soft)',
  color: confirmed ? 'var(--c-success-fg)' : 'var(--c-warning-fg)' });
const banner = color => ({ margin: '4px 0', padding: '8px 10px', borderRadius: 6, borderInlineStart: `4px solid ${color}`,
  background: 'var(--c-bg-subtle)', fontSize: 14 });
const targetBanner = { ...banner('#d81b60'), display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' };
// Read aloud, not shown: the numbers already say they are a distance.
const srLabel = { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap' };
const mapBox = { position: 'relative', height: 'calc(100dvh - 300px)', minHeight: 420, borderRadius: 10, overflow: 'hidden',
  border: '1px solid var(--c-border)' };
const hint = { position: 'absolute', bottom: 28, insetInline: 10, margin: 0, zIndex: 900, textAlign: 'center',
  background: 'rgba(0,0,0,.7)', color: '#fff', padding: '6px 10px', borderRadius: 6, fontSize: 13, pointerEvents: 'none' };
const choice = { position: 'absolute', insetInline: 10, bottom: 10, zIndex: 1000, display: 'flex', flexDirection: 'column',
  gap: 8, padding: 12, borderRadius: 10, background: 'var(--c-surface)', color: 'var(--c-text-fg)',
  boxShadow: '0 4px 18px rgba(0,0,0,.3)' };
const btn = bg => ({ padding: '10px 14px', minHeight: 44, borderRadius: 6, border: 'none', background: bg, color: '#fff',
  fontSize: 14, cursor: 'pointer' });
